import { ReactNode, useContext } from 'react';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { RepeatIcon } from '@chakra-ui/icons';

import ActurisConfigBlocker from 'views/quote_marketplace/components/ActurisConfigBlocker';
import { useActurisConfigStatus } from 'views/quote_marketplace/hooks';

import { AuthContext } from 'contexts/AuthContext';
import { ROLE_ADMIN } from 'variables/constants';

interface ActurisConfigGateProps {
  children: ReactNode;
}

const ActurisConfigGate = ({ children }: ActurisConfigGateProps) => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === ROLE_ADMIN;

  const {
    syncStatus,
    syncError,
    canAccess,
    missingFields,
    message,
    isLoading,
    error,
    refetch,
    resync,
  } = useActurisConfigStatus();

  if (isLoading) {
    return (
      <Flex justify="center" py="40px">
        <Spinner size="md" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p="20px">
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Unable to check Acturis configuration</AlertTitle>
            <AlertDescription>
              Unable to check your Acturis configuration status.
            </AlertDescription>
          </Box>
          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            leftIcon={<RepeatIcon />}
            onClick={() => refetch()}
          >
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  if (syncStatus === 'error') {
    return (
      <Box p="20px">
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle>Acturis Sync Failed</AlertTitle>
            <AlertDescription>
              {syncError || 'Failed to sync your Acturis configuration.'}
            </AlertDescription>
          </Box>
          <Button
            size="sm"
            colorScheme="red"
            variant="outline"
            leftIcon={<RepeatIcon />}
            onClick={() => resync()}
          >
            Retry
          </Button>
        </Alert>
      </Box>
    );
  }

  if (syncStatus === 'syncing') {
    return (
      <Box textAlign="center" py="40px">
        <Spinner size="md" />
        <Text mt={2}>Syncing your Acturis configuration...</Text>
      </Box>
    );
  }

  if (!canAccess) {
    return (
      <Box p="20px">
        <ActurisConfigBlocker
          missingFields={missingFields}
          message={message ?? 'Contact your admin to complete Acturis setup.'}
          isAdmin={isAdmin}
          userId={user?.id}
        />
      </Box>
    );
  }

  return <>{children}</>;
};

export default ActurisConfigGate;
